import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  Loader2, 
  RefreshCw,
  Plus,
  Minus,
  AlertTriangle,
  History,
  Package,
  ScanBarcode
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import BarcodeScanner from '@/components/BarcodeScanner';
import { useModulePermissions } from '@/hooks/useModulePermissions';

var API = process.env.REACT_APP_BACKEND_URL + '/api';

function StockAdjustment() {
  var modulePerms = useModulePermissions();
  var canEditInventory = modulePerms.canEdit('inventory');
  var loadingState = React.useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  
  var historyState = React.useState([]);
  var adjustmentHistory = historyState[0];
  var setAdjustmentHistory = historyState[1];
  
  var searchState = React.useState('');
  var searchTerm = searchState[0];
  var setSearchTerm = searchState[1];
  
  var productsState = React.useState([]);
  var searchResults = productsState[0];
  var setSearchResults = productsState[1];
  
  var selectedState = React.useState(null);
  var selectedProduct = selectedState[0];
  var setSelectedProduct = selectedState[1];
  
  var dialogState = React.useState(false);
  var dialogOpen = dialogState[0];

  // Bulk CSV upload state (May 2026 — fix many products at once after the
  // batch-adjustment bug was patched).
  var bulkRowsState = React.useState([]);
  var bulkRows = bulkRowsState[0];
  var setBulkRows = bulkRowsState[1];
  var bulkResultState = React.useState(null);
  var bulkResult = bulkResultState[0];
  var setBulkResult = bulkResultState[1];
  var bulkBusyState = React.useState(false);
  var bulkBusy = bulkBusyState[0];
  var setBulkBusy = bulkBusyState[1];

  function parseBulkCsv(text) {
    // Expected header: SKU, Target Stock, Reason, Comments
    // Reason and Comments are optional (default reason = count_correction).
    var lines = text.split(/\r?\n/).filter(function(l) { return l.trim().length > 0; });
    if (lines.length < 2) { toast.error('CSV must have a header row + at least one data row'); return []; }
    var headerCols = lines[0].split(',').map(function(c) { return c.trim().toLowerCase(); });
    var skuIdx = headerCols.findIndex(function(c) { return c === 'sku' || c === 'product sku' || c === 'product_id'; });
    var stockIdx = headerCols.findIndex(function(c) { return c === 'target stock' || c === 'target_stock' || c === 'stock' || c === 'qty'; });
    var reasonIdx = headerCols.findIndex(function(c) { return c === 'reason'; });
    var commentsIdx = headerCols.findIndex(function(c) { return c === 'comments' || c === 'comment' || c === 'notes'; });
    if (skuIdx < 0 || stockIdx < 0) { toast.error('CSV must include "SKU" and "Target Stock" columns'); return []; }
    var rows = [];
    for (var i = 1; i < lines.length; i++) {
      var cols = lines[i].split(',').map(function(c) { return c.trim(); });
      var sku = cols[skuIdx];
      var target = parseFloat(cols[stockIdx]);
      if (!sku || isNaN(target) || target < 0) continue;
      rows.push({
        sku: sku, target_stock: target,
        reason: (reasonIdx >= 0 && cols[reasonIdx]) ? cols[reasonIdx] : 'count_correction',
        comments: (commentsIdx >= 0 ? cols[commentsIdx] : '') || 'Bulk CSV adjustment',
      });
    }
    return rows;
  }

  function handleBulkFile(file) {
    if (!file) return;
    var reader = new FileReader();
    reader.onload = function(e) {
      var rows = parseBulkCsv(String(e.target.result || ''));
      setBulkRows(rows);
      setBulkResult(null);
      if (rows.length > 0) toast.success('Parsed ' + rows.length + ' row(s). Click "Preview" to validate.');
    };
    reader.readAsText(file);
  }

  function runBulk(dryRun) {
    if (bulkRows.length === 0) { toast.error('Upload a CSV first'); return; }
    setBulkBusy(true);
    var token = localStorage.getItem('token');
    axios.post(API + '/inventory/adjust-stock/bulk', { rows: bulkRows, dry_run: dryRun }, { headers: { Authorization: 'Bearer ' + token } })
      .then(function(res) {
        setBulkResult(res.data);
        toast.success((dryRun ? 'Preview: ' : 'Applied: ') + res.data.succeeded_count + ' OK, ' + res.data.failed_count + ' failed');
        if (!dryRun) {
          fetchAdjustmentHistory();
          setBulkRows([]);
        }
      })
      .catch(function(err) { toast.error(err.response && err.response.data ? err.response.data.detail : 'Bulk failed'); })
      .finally(function() { setBulkBusy(false); });
  }

  function downloadBulkTemplate() {
    var csv = 'SKU,Target Stock,Reason,Comments\nPROD-018041,0,count_correction,No stock available\nPROD-018042,50,count_correction,Recount\n';
    var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url; a.download = 'stock_adjustment_template.csv'; a.click();
    URL.revokeObjectURL(url);
  }
  var setDialogOpen = dialogState[1];
  
  var adjustmentState = React.useState({
    adjustment_qty: 0,
    reason: '',
    comments: ''
  });
  var adjustment = adjustmentState[0];
  var setAdjustment = adjustmentState[1];
  
  var submittingState = React.useState(false);
  var submitting = submittingState[0];
  var setSubmitting = submittingState[1];
  
  var userRoleState = React.useState('user');
  var userRole = userRoleState[0];
  var setUserRole = userRoleState[1];

  var scannerModeState = React.useState(false);
  var scannerMode = scannerModeState[0];
  var setScannerMode = scannerModeState[1];

  React.useEffect(function() {
    fetchAdjustmentHistory();
    try {
      var token = localStorage.getItem('token');
      if (token) {
        var payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role || 'user');
      }
    } catch (e) {}
  }, []);

  function fetchAdjustmentHistory() {
    var token = localStorage.getItem('token');
    axios.get(API + '/inventory/adjustments', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      setAdjustmentHistory(response.data || []);
    }).catch(function() {
      /* History fetch failed — silent, retries on next open. */
    });
  }

  function searchProducts() {
    if (!searchTerm) return;
    setLoading(true);
    var token = localStorage.getItem('token');
    axios.post(API + '/inventory/stock-list', {
      search: searchTerm,
      page: 1,
      page_size: 10
    }, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      setSearchResults(response.data.items || []);
      setLoading(false);
    }).catch(function(error) {
      toast.error('Search failed');
      setLoading(false);
    });
  }

  function selectProduct(product) {
    setSelectedProduct(product);
    setSearchResults([]);
    setSearchTerm('');
    setAdjustment({ adjustment_qty: 0, reason: '', comments: '' });
    setDialogOpen(true);
  }

  function handleBarcodeProductFound(product) {
    // When barcode scanned, select product for adjustment
    selectProduct(product);
  }

  function submitAdjustment() {
    if (!selectedProduct) return;
    if (!adjustment.reason) {
      toast.error('Please select a reason');
      return;
    }
    if (adjustment.adjustment_qty === 0) {
      toast.error('Adjustment quantity cannot be zero');
      return;
    }
    
    setSubmitting(true);
    var token = localStorage.getItem('token');
    axios.post(API + '/inventory/adjust-stock', {
      product_id: selectedProduct.id,
      adjustment_qty: adjustment.adjustment_qty,
      reason: adjustment.reason,
      comments: adjustment.comments
    }, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      toast.success('Stock adjusted successfully');
      setDialogOpen(false);
      setSelectedProduct(null);
      fetchAdjustmentHistory();
      setSubmitting(false);
    }).catch(function(error) {
      toast.error(error.response?.data?.detail || 'Adjustment failed');
      setSubmitting(false);
    });
  }

  function getReasonLabel(reason) {
    var labels = {
      'damaged': 'Damaged Goods',
      'expired': 'Expired Items',
      'count_correction': 'Stock Count Correction',
      'theft': 'Theft/Loss',
      'return': 'Customer Return',
      'other': 'Other'
    };
    return labels[reason] || reason;
  }

  if (userRole !== 'admin') {
    return React.createElement('div', { className: 'flex flex-col items-center justify-center h-64' },
      React.createElement(AlertTriangle, { className: 'w-16 h-16 text-amber-500 mb-4' }),
      React.createElement('h2', { className: 'text-xl font-semibold text-slate-700' }, 'Admin Access Required'),
      React.createElement('p', { className: 'text-slate-500' }, 'Only administrators can perform stock adjustments')
    );
  }

  return React.createElement('div', { className: 'space-y-6', 'data-testid': 'stock-adjustment' },
    // Header
    React.createElement('div', { className: 'flex items-center justify-between' },
      React.createElement('div', null,
        React.createElement('h1', { className: 'text-3xl font-heading font-bold text-primary' }, 'Stock Adjustment'),
        React.createElement('p', { className: 'text-slate-600 mt-1' }, 'Manual stock corrections and adjustments (Admin Only)')
      ),
      React.createElement('div', { className: 'flex gap-2' },
        React.createElement(Button, { 
          variant: scannerMode ? 'default' : 'outline', 
          onClick: function() { setScannerMode(!scannerMode); }, 
          className: 'gap-2 ' + (scannerMode ? 'bg-green-600 hover:bg-green-700' : ''),
          'data-testid': 'toggle-scanner-btn'
        },
          React.createElement(ScanBarcode, { className: 'w-4 h-4' }),
          scannerMode ? 'Scanner ON' : 'Scanner Mode'
        ),
        React.createElement(Button, { variant: 'outline', onClick: fetchAdjustmentHistory, className: 'gap-2' },
          React.createElement(RefreshCw, { className: 'w-4 h-4' }),
          'Refresh'
        )
      )
    ),

    // Barcode Scanner Section (when enabled)
    scannerMode && React.createElement(Card, { className: 'bg-green-50 border-green-200' },
      React.createElement(CardContent, { className: 'p-4' },
        React.createElement('div', { className: 'flex items-center gap-2 mb-3' },
          React.createElement(ScanBarcode, { className: 'w-5 h-5 text-green-600' }),
          React.createElement('h3', { className: 'font-semibold text-green-800' }, 'Barcode Scanner Mode'),
          React.createElement(Badge, { className: 'bg-green-600' }, 'Active')
        ),
        React.createElement(BarcodeScanner, {
          onProductFound: handleBarcodeProductFound,
          placeholder: 'Scan barcode to select product for adjustment...',
          autoFocus: true,
          showProductPreview: false
        })
      )
    ),

    // Search Product Section
    React.createElement(Card, null,
      React.createElement(CardHeader, null,
        React.createElement(CardTitle, { className: 'text-lg' }, 'Adjust Stock for Product')
      ),
      React.createElement(CardContent, null,
        React.createElement('div', { className: 'space-y-4' },
          React.createElement('div', { className: 'flex gap-2' },
            React.createElement('div', { className: 'flex-1 relative' },
              React.createElement(Search, { className: 'absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4' }),
              React.createElement(Input, {
                placeholder: 'Search product by SKU, name, or barcode...',
                value: searchTerm,
                onChange: function(e) { setSearchTerm(e.target.value); },
                onKeyDown: function(e) { if (e.key === 'Enter') searchProducts(); },
                className: 'pl-9'
              })
            ),
            React.createElement(Button, { onClick: searchProducts, disabled: loading },
              loading ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : 'Search'
            )
          ),
          
          // Search Results
          searchResults.length > 0 && React.createElement('div', { className: 'border rounded-lg divide-y' },
            searchResults.map(function(product) {
              return React.createElement('div', { 
                key: product.id,
                className: 'flex items-center justify-between p-3 hover:bg-slate-50 cursor-pointer',
                onClick: function() { selectProduct(product); }
              },
                React.createElement('div', null,
                  React.createElement('p', { className: 'font-medium' }, product.name),
                  React.createElement('p', { className: 'text-sm text-slate-500' }, 
                    product.product_id, ' • ', product.brand, ' • Stock: ', product.current_stock, ' ', product.measurement_unit || product.unit
                  )
                ),
                React.createElement(Button, { size: 'sm', variant: 'outline' }, 'Select')
              );
            })
          )
        )
      )
    ),

    // Bulk CSV Adjustment (admin-only, mirrors the per-row fix logic)
    React.createElement(Card, { className: 'border-indigo-200 bg-indigo-50/40' },
      React.createElement(CardHeader, null,
        React.createElement(CardTitle, { className: 'text-lg flex items-center gap-2' },
          React.createElement(Package, { className: 'w-5 h-5 text-indigo-700' }),
          'Bulk Stock Adjustment (CSV)'
        )
      ),
      React.createElement(CardContent, null,
        React.createElement('div', { className: 'space-y-3' },
          React.createElement('p', { className: 'text-sm text-slate-600' },
            'Upload a CSV with columns: ',
            React.createElement('code', { className: 'bg-white px-1 rounded text-xs' }, 'SKU, Target Stock, Reason, Comments'),
            '. Each row sets the product\'s stock to the exact target qty. Same FIFO batch-aware logic as the single-product form.'
          ),
          React.createElement('div', { className: 'flex flex-wrap gap-2 items-center' },
            React.createElement(Button, { variant: 'outline', size: 'sm', onClick: downloadBulkTemplate, 'data-testid': 'bulk-adj-template-btn' },
              'Download Template'
            ),
            React.createElement('input', {
              type: 'file', accept: '.csv,text/csv',
              onChange: function(e) { handleBulkFile(e.target.files && e.target.files[0]); },
              className: 'text-sm',
              'data-testid': 'bulk-adj-file-input',
            }),
            React.createElement('span', { className: 'text-xs text-slate-500' },
              bulkRows.length > 0 ? bulkRows.length + ' row(s) parsed' : 'No file loaded'
            )
          ),
          bulkRows.length > 0 && React.createElement('div', { className: 'flex gap-2' },
            React.createElement(Button, { variant: 'outline', onClick: function() { runBulk(true); }, disabled: bulkBusy, 'data-testid': 'bulk-adj-preview-btn' },
              bulkBusy ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : 'Preview (dry-run)'
            ),
            React.createElement(Button, { onClick: function() { runBulk(false); }, disabled: bulkBusy, className: 'bg-indigo-600 hover:bg-indigo-700', 'data-testid': 'bulk-adj-apply-btn' },
              bulkBusy ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : 'Apply Adjustments'
            )
          ),
          bulkResult && React.createElement('div', { className: 'mt-3 p-3 bg-white border rounded-lg text-sm' },
            React.createElement('p', { className: 'font-semibold mb-1' },
              (bulkResult.dry_run ? 'Preview result: ' : 'Applied: '),
              React.createElement('span', { className: 'text-green-700' }, bulkResult.succeeded_count + ' OK'),
              ' · ',
              React.createElement('span', { className: 'text-red-700' }, bulkResult.failed_count + ' failed')
            ),
            bulkResult.failed && bulkResult.failed.length > 0 && React.createElement('ul', { className: 'text-xs text-red-700 list-disc ml-5 max-h-40 overflow-auto' },
              bulkResult.failed.map(function(f, i) {
                return React.createElement('li', { key: i }, 'Row ', f.row, ' (', f.ref, '): ', f.error);
              })
            ),
            bulkResult.succeeded && bulkResult.succeeded.length > 0 && React.createElement('details', { className: 'mt-2' },
              React.createElement('summary', { className: 'cursor-pointer text-slate-700 text-xs' }, 'View ' + bulkResult.succeeded.length + ' successful row(s)'),
              React.createElement('ul', { className: 'text-xs text-slate-700 list-disc ml-5 max-h-40 overflow-auto' },
                bulkResult.succeeded.map(function(s, i) {
                  return React.createElement('li', { key: i },
                    s.sku, ' (', s.product_name, '): ',
                    s.previous_stock, ' → ', (s.new_stock !== undefined ? s.new_stock : s.target_stock),
                    ' (Δ ', s.delta, ')'
                  );
                })
              )
            )
          )
        )
      )
    ),

    // Adjustment History
    React.createElement(Card, null,
      React.createElement(CardHeader, null,
        React.createElement(CardTitle, { className: 'flex items-center gap-2' },
          React.createElement(History, { className: 'w-5 h-5' }),
          'Recent Adjustments'
        )
      ),
      React.createElement(CardContent, null,
        adjustmentHistory.length === 0 
          ? React.createElement('p', { className: 'text-slate-500 text-center py-8' }, 'No adjustments recorded yet')
          : React.createElement('div', { className: 'space-y-3' },
              adjustmentHistory.slice(0, 20).map(function(adj) {
                var isPositive = adj.adjustment_qty > 0;
                return React.createElement('div', { 
                  key: adj.id,
                  className: 'flex items-center justify-between p-4 border rounded-lg'
                },
                  React.createElement('div', { className: 'flex items-center gap-4' },
                    React.createElement('div', { 
                      className: 'w-10 h-10 rounded-full flex items-center justify-center ' + 
                        (isPositive ? 'bg-green-100' : 'bg-red-100')
                    },
                      isPositive 
                        ? React.createElement(Plus, { className: 'w-5 h-5 text-green-600' })
                        : React.createElement(Minus, { className: 'w-5 h-5 text-red-600' })
                    ),
                    React.createElement('div', null,
                      React.createElement('p', { className: 'font-medium' }, adj.product_name),
                      React.createElement('p', { className: 'text-sm text-slate-500' }, 
                        adj.product_sku, ' • ', getReasonLabel(adj.reason)
                      ),
                      adj.comments && React.createElement('p', { className: 'text-xs text-slate-400 mt-1' }, 
                        '"', adj.comments, '"'
                      )
                    )
                  ),
                  React.createElement('div', { className: 'text-right' },
                    React.createElement('p', { className: 'font-semibold ' + (isPositive ? 'text-green-600' : 'text-red-600') },
                      isPositive ? '+' : '', adj.adjustment_qty
                    ),
                    React.createElement('p', { className: 'text-sm text-slate-500' },
                      adj.previous_stock, ' → ', adj.new_stock
                    ),
                    React.createElement('p', { className: 'text-xs text-slate-400' },
                      adj.timestamp?.slice(0, 16).replace('T', ' ')
                    ),
                    React.createElement('p', { className: 'text-xs text-slate-400' },
                      'By: ', adj.adjusted_by_name
                    )
                  )
                );
              })
            )
      )
    ),

    // Adjustment Dialog
    React.createElement(Dialog, { open: dialogOpen, onOpenChange: setDialogOpen },
      React.createElement(DialogContent, { className: 'max-w-md' },
        React.createElement(DialogHeader, null,
          React.createElement(DialogTitle, { className: 'flex items-center gap-2' },
            React.createElement(Package, { className: 'w-5 h-5' }),
            'Adjust Stock'
          )
        ),
        selectedProduct && React.createElement('div', { className: 'space-y-4' },
          // Product Info
          React.createElement('div', { className: 'p-4 bg-slate-50 rounded-lg' },
            React.createElement('p', { className: 'font-semibold' }, selectedProduct.name),
            React.createElement('p', { className: 'text-sm text-slate-600' }, 
              selectedProduct.product_id, ' • ', selectedProduct.brand
            ),
            React.createElement('p', { className: 'text-lg font-bold mt-2' }, 
              'Current Stock: ', selectedProduct.current_stock, ' ', selectedProduct.unit
            )
          ),
          
          // Adjustment Quantity
          React.createElement('div', { className: 'space-y-2' },
            React.createElement(Label, null, 'Adjustment Quantity'),
            React.createElement('div', { className: 'flex items-center gap-2' },
              React.createElement(Button, {
                type: 'button',
                variant: 'outline',
                size: 'sm',
                onClick: function() { 
                  setAdjustment(function(prev) { 
                    return Object.assign({}, prev, { adjustment_qty: prev.adjustment_qty - 1 }); 
                  }); 
                }
              }, React.createElement(Minus, { className: 'w-4 h-4' })),
              React.createElement(Input, {
                type: 'number',
                value: adjustment.adjustment_qty,
                onChange: function(e) { 
                  setAdjustment(function(prev) { 
                    return Object.assign({}, prev, { adjustment_qty: parseInt(e.target.value) || 0 }); 
                  }); 
                },
                className: 'w-24 text-center'
              }),
              React.createElement(Button, {
                type: 'button',
                variant: 'outline',
                size: 'sm',
                onClick: function() { 
                  setAdjustment(function(prev) { 
                    return Object.assign({}, prev, { adjustment_qty: prev.adjustment_qty + 1 }); 
                  }); 
                }
              }, React.createElement(Plus, { className: 'w-4 h-4' }))
            ),
            React.createElement('p', { className: 'text-sm text-slate-500' },
              'New stock will be: ', 
              React.createElement('span', { className: 'font-semibold' }, 
                selectedProduct.current_stock + adjustment.adjustment_qty
              )
            )
          ),
          
          // Reason
          React.createElement('div', { className: 'space-y-2' },
            React.createElement(Label, null, 'Reason *'),
            React.createElement('select', {
              value: adjustment.reason,
              onChange: function(e) { 
                setAdjustment(function(prev) { 
                  return Object.assign({}, prev, { reason: e.target.value }); 
                }); 
              },
              className: 'w-full h-10 px-3 border rounded-md'
            },
              React.createElement('option', { value: '' }, 'Select reason...'),
              React.createElement('option', { value: 'damaged' }, 'Damaged Goods'),
              React.createElement('option', { value: 'expired' }, 'Expired Items'),
              React.createElement('option', { value: 'count_correction' }, 'Stock Count Correction'),
              React.createElement('option', { value: 'theft' }, 'Theft / Loss'),
              React.createElement('option', { value: 'return' }, 'Customer Return'),
              React.createElement('option', { value: 'other' }, 'Other')
            )
          ),
          
          // Comments
          React.createElement('div', { className: 'space-y-2' },
            React.createElement(Label, null, 'Comments'),
            React.createElement('textarea', {
              value: adjustment.comments,
              onChange: function(e) { 
                setAdjustment(function(prev) { 
                  return Object.assign({}, prev, { comments: e.target.value }); 
                }); 
              },
              placeholder: 'Additional notes...',
              className: 'w-full h-20 px-3 py-2 border rounded-md resize-none'
            })
          ),
          
          // Warning
          adjustment.adjustment_qty < 0 && React.createElement('div', { className: 'flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg' },
            React.createElement(AlertTriangle, { className: 'w-5 h-5 text-amber-600' }),
            React.createElement('p', { className: 'text-sm text-amber-800' }, 
              'This will reduce stock by ', Math.abs(adjustment.adjustment_qty), ' units'
            )
          ),
          
          // Actions
          React.createElement('div', { className: 'flex justify-end gap-2 pt-4 border-t' },
            React.createElement(Button, { variant: 'outline', onClick: function() { setDialogOpen(false); } }, 'Cancel'),
            canEditInventory && React.createElement(Button, { 
              onClick: submitAdjustment, 
              disabled: submitting || !adjustment.reason || adjustment.adjustment_qty === 0,
              className: adjustment.adjustment_qty < 0 ? 'bg-red-600 hover:bg-red-700' : ''
            },
              submitting ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin mr-2' }) : null,
              'Confirm Adjustment'
            )
          )
        )
      )
    )
  );
}

export default StockAdjustment;
